import { useEffect, useState } from 'react';
import { env } from '../lib/env.js';

/**
 * Runtime probe — returns `true` only when the server's `/api/calls/enabled`
 * route exists and responds `{ enabled: true }`.
 *
 * The route is only registered when the server is configured with
 * `CONFERWEB_API_BASE` (see `apps/server/src/routes/conferweb.ts`), so an
 * unconfigured deploy returns 404 and this hook returns `false`, hiding the
 * Call button without any client-side configuration.
 *
 * The URL is built from `env.apiUrl` (absolute on desktop) — a relative
 * `/api/calls/enabled` would resolve to `tauri://localhost` in the desktop app
 * and always fail, permanently hiding the Call button there.
 *
 * Defaults to `false` until the probe resolves.
 */
export function useCallsEnabled(): boolean {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch(`${env.apiUrl}/api/calls/enabled`, { credentials: 'include' })
      .then(async (res) => {
        if (!res.ok) return;
        const body = await res.json() as unknown;
        if (!cancelled && typeof body === 'object' && body !== null && (body as Record<string, unknown>)['enabled'] === true) {
          setEnabled(true);
        }
      })
      .catch(() => { /* network error → keep enabled=false */ });
    return () => { cancelled = true; };
  }, []);

  return enabled;
}
